import { Injectable } from "@angular/core";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { Router } from "@angular/router";
import {
  EventMessage,
  EventType,
  InteractionStatus,
} from "@azure/msal-browser";
import {
  BehaviorSubject,
  filter,
  firstValueFrom,
  Observable,
  of,
  take,
  throwError,
} from "rxjs";
import { LoggerService } from "../services/logger.service";
import { TokenService } from "./token.service";
import { UserService } from "../user/user.service";

@Injectable({ providedIn: "root" })
export class AuthService {
  private isLoggedIn: boolean = false;
  private isLoginInProgress: boolean = false;
  private readonly isLoggedInSubject = new BehaviorSubject<boolean>(
    this.isLoggedIn,
  );
  isAuthenticated$ = this.isLoggedInSubject.asObservable();

  _authenticated: boolean;

  constructor(
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly logger: LoggerService,
    private readonly tokenService: TokenService,
    private readonly userService: UserService,
    private readonly router: Router,
  ) {
    const activeAccount = this.msalService.instance.getActiveAccount();
    this.isLoggedIn = !!activeAccount;
    this.updateLoginState();
    this.setupMsalEventSubscriptions();
  }

  private setupMsalEventSubscriptions(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS,
        ),
      )
      .subscribe(() => {
        this.logger.info("AuthService", "LOGIN_SUCCESS event handled");
        this.handleSuccessfulLogin();
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
      )
      .subscribe(() => {
        this.updateLoginState();
      });
  }

  private handleSuccessfulLogin(): void {
    this.isLoggedIn = true;
    this.updateLoginState();
    // Portal role is resolved by AuthGuard.ensurePortalRole() which runs during routing.
    // Calling it here as well caused a race condition and double API call on first login.
    // need to uncomment the following line of code once authorization will be enabled
    //this.extractClaims();
    // this.systemService.getSystems().subscribe(); // comment by asif
    this.logger.debug("AuthService", "Login completed successfully");
  }

  private resolvePortalRoleAfterLogin(): void {
    this.userService
      .get()
      .pipe(take(1))
      .subscribe({
        next: (user) => {
          const portalRole = String(user?.portalRole ?? "").trim();
          if (!portalRole) {
            this.logger.warn("AuthService", "No portalRole assigned; redirecting to no-access");
            this.router.navigate(["/no-access"]);
          }
        },
        error: (error) => {
          this.logger.error("AuthService", "Failed to load contact role after login:", error);
          this.router.navigate(["/no-access"]);
        },
      });
  }

  private updateLoginState(): void {
    this.isLoggedInSubject.next(this.isLoggedIn);
  }

  async login(): Promise<void> {
    if (this.isLoginInProgress) {
      this.logger.warn("AuthService", "Login attempt already in progress");
      return;
    }

    try {
      this.isLoginInProgress = true;
      const interactionStatus = await firstValueFrom(
        this.msalBroadcastService.inProgress$,
      );

      if (interactionStatus !== InteractionStatus.None) {
        this.logger.warn("AuthService", "Existing interaction in progress");
        return;
      }

      this.logger.info("AuthService", "Initiating login redirect");
      await this.msalService.loginRedirect();
    } catch (error) {
      this.logger.error("AuthService", "Login failed:", error);
      throw error;
    } finally {
      this.isLoginInProgress = false;
    }
  }

  signIn(credentials: { email: string; password: string }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this.isAuthenticated$) {
      return throwError("User is already logged in.");
    }
  }

  logout(): void {
    this.logger.info("AuthService", "Initiating logout");
    localStorage.removeItem("userLang");
    localStorage.removeItem("plant_id");
    localStorage.removeItem("loggedInUser");
    localStorage.removeItem("userInfo");
    this.tokenService.removeToken();
    this.msalService.logoutRedirect();
    this.isLoggedIn = false;
    this.updateLoginState();
  }

  isFirstLogin(email: string) {
    return of({ isFirstLogin: false });
  }

  getCurrentUser(): { username: string } | null {
    const account = this.msalService.instance.getActiveAccount();
    return account ? { username: account.username } : null;
  }

  forgotPassword(email: string): void {}
  resetPassword(newPassword: string): void {}
  signUp(userData: any): void {}
  unlockSession(data: any): void {}
}
